import { ClipboardCheck, GraduationCap, Users, Rocket, Radio, CheckCircle2, Flag } from "lucide-react";
import { implementationStrategy, implementationGateChecks } from "@/lib/proposalData";
import { useDeployment } from "@/lib/DeploymentContext";

const ICONS = {
  blueprint: ClipboardCheck,
  graduation: GraduationCap,
  users: Users,
  rocket: Rocket,
  broadcast: Radio,
};

const fmt = (n) => `$${n.toLocaleString()}`;

export default function ImplementationStrategy() {
  const d = useDeployment();
  return (
    <section
      id="implementation"
      data-testid="section-implementation"
      className="bg-[#F9F7F1] py-24 lg:py-32 border-y border-[#CBD5E1]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Heading */}
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-8 bg-[#8B0000]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000]">
                Implementation Strategy · 12-Month Launch Arc
              </span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              {d.implementationOpening} <em className="italic">resolution</em><br />
              to 1,500 households served.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-3 text-[#334155] leading-relaxed">
            <p>
              Five overlapping phases executed inside Year 1, driven by the
              Sterile Mate Developer Group's firm Develop · Build · Train
              engagement. Every phase ends with a signed deliverable; every
              quarter ends with a Go / No-Go gate logged to City Council.
            </p>
          </div>
        </div>

        {/* Phase Cards */}
        <ol className="grid lg:grid-cols-5 gap-px bg-[#CBD5E1] border border-[#CBD5E1] mb-14">
          {implementationStrategy.map((p) => {
            const Icon = ICONS[p.icon] || ClipboardCheck;
            return (
              <li
                key={p.id}
                data-testid={`impl-phase-${p.id}`}
                className="bg-white p-6 lg:p-7 flex flex-col"
              >
                <div className="flex items-center justify-between mb-4">
                  <Icon className="h-6 w-6 text-[#8B0000]" />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000]">
                    Phase {p.id}
                  </span>
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mb-1">
                  {p.period}
                </div>
                <h3 className="font-display text-lg text-[#0A192F] leading-snug mb-2">
                  {p.title}
                </h3>
                <div className="font-mono text-[10px] uppercase tracking-wider text-[#64748B] mb-3 leading-snug">
                  Lead · {p.lead}
                </div>
                <p className="text-xs text-[#475569] leading-relaxed flex-1">
                  {p.body}
                </p>
                <div className="mt-4 pt-3 border-t border-[#CBD5E1]">
                  <div className="font-mono text-[9px] uppercase tracking-widest text-[#8B0000] mb-2">
                    Deliverables
                  </div>
                  <ul className="space-y-1.5">
                    {p.deliverables.map((d, i) => (
                      <li key={i} className="flex items-start gap-2 text-[11px] text-[#334155] leading-snug">
                        <CheckCircle2 className="h-3 w-3 text-[#8B0000] shrink-0 mt-0.5" />
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-4 pt-3 border-t border-[#CBD5E1] font-mono text-[10px] uppercase tracking-widest">
                  {p.cost ? (
                    <span className="text-[#0A192F]">Budget · {fmt(p.cost)}</span>
                  ) : (
                    <span className="text-[#64748B]">Sourced from {p.sourceFrom}</span>
                  )}
                </div>
              </li>
            );
          })}
        </ol>

        {/* Gate Checks */}
        <div data-testid="impl-gates" className="bg-[#0A192F] text-[#F9F7F1] p-7 lg:p-10 relative overflow-hidden">
          <div className="absolute inset-0 grain-overlay opacity-20 pointer-events-none" />
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
              <Flag className="h-5 w-5 text-[#D4AF37]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">
                City Council Go / No-Go Gates
              </span>
            </div>
            <h3 className="font-display text-2xl lg:text-3xl leading-tight mb-6 max-w-3xl">
              Three formal decision gates. Every one of them logged to City Council.
            </h3>
            <ul className="grid md:grid-cols-3 gap-px bg-[#F9F7F1]/15 border border-[#F9F7F1]/15">
              {implementationGateChecks.map((g, i) => (
                <li
                  key={g.gate}
                  data-testid={`impl-gate-${i}`}
                  className="bg-[#0A192F] p-5 lg:p-6"
                >
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-2">
                    {g.gate}
                  </div>
                  <div className="font-display text-base text-[#F9F7F1] mb-2 leading-snug">
                    {g.name}
                  </div>
                  <p className="text-xs text-[#CBD5E1] leading-relaxed">
                    {g.criteria}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
